
import { useState, useEffect } from "react"
import {
  Building2,
  Users,
  Target,
  ArrowDown,
  GitBranch,
  Search,
  Plus,
  CheckCircle,
  XCircle,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  AlertTriangle,
  Info,
  Download,
} from "lucide-react"

interface VendorIntakeForm {
  id: string
  alias: string
  formstackUrl: string
  lawFirmName: string
  clientEligibility: string
  maxClientsPerIntake: number
  priority: number
  campaignAssignment: string
  bonusPerQualifiedSale: number
  isActive: boolean
  isHidden: boolean
  createdAt: string
  updatedAt: string
}

interface Organization {
  id: string
  name: string
  type: "EM" | "DM" | "CM" | "BM" | "AM" | "M"
  parentId?: string
  managerId: string
  managerName: string
  isActive: boolean
  agentCount: number
  level: number
  children?: Organization[]
}

interface FormAssignment {
  id: string
  formId: string
  formAlias: string
  organizationId: string
  organizationName: string
  organizationType: string
  assignedAt: string
  assignedBy: string
  isActive: boolean
  isInherited: boolean
  parentAssignmentId?: string
  directUsersAffected: number
  inheritedUsersAffected: number
  totalUsersAffected: number
}

interface AssignmentPreview {
  directUsers: number
  inheritedUsers: number
  totalUsers: number
  affectedOrganizations: Organization[]
  inheritedOrganizations: Organization[]
}

export default function FMOVendorFormAssignment() {
  // Sample data
  const [forms] = useState<VendorIntakeForm[]>([
    {
      id: "form-001",
      alias: "Mass Tort Gaming Addiction A",
      formstackUrl: "https://formstack.com/forms/gaming-addiction-intake-a",
      lawFirmName: "Smith & Associates Law Firm",
      clientEligibility: "Ages 18-65, PlayStation/Xbox/PC, FIFA/Madden/NBA2K series, minimum 2 hours daily play",
      maxClientsPerIntake: 5,
      priority: 1,
      campaignAssignment: "Mass Tort Gaming Campaign 2024",
      bonusPerQualifiedSale: 150.0,
      isActive: true,
      isHidden: false,
      createdAt: "2024-01-15T10:00:00Z",
      updatedAt: "2024-01-20T14:30:00Z",
    },
    {
      id: "form-002",
      alias: "Medicare Advantage Enrollment",
      formstackUrl: "https://formstack.com/forms/medicare-advantage-intake",
      lawFirmName: "Johnson Legal Group",
      clientEligibility: "Ages 65+, Medicare eligible, residing in target states (FL, TX, CA, NY)",
      maxClientsPerIntake: 3,
      priority: 2,
      campaignAssignment: "Medicare Advantage 2024",
      bonusPerQualifiedSale: 200.0,
      isActive: true,
      isHidden: false,
      createdAt: "2024-01-10T09:00:00Z",
      updatedAt: "2024-01-18T16:45:00Z",
    },
    {
      id: "form-003",
      alias: "Life Insurance Application",
      formstackUrl: "https://formstack.com/forms/life-insurance-comprehensive",
      lawFirmName: "Williams & Partners",
      clientEligibility: "Ages 25-70, good health status, minimum $50k annual income, non-smoker preferred",
      maxClientsPerIntake: 2,
      priority: 3,
      campaignAssignment: "Life Insurance Premium Campaign",
      bonusPerQualifiedSale: 300.0,
      isActive: true,
      isHidden: false,
      createdAt: "2024-01-05T11:30:00Z",
      updatedAt: "2024-01-22T13:15:00Z",
    },
    {
      id: "form-004",
      alias: "Auto Insurance Claims",
      formstackUrl: "https://formstack.com/forms/auto-insurance-claims-intake",
      lawFirmName: "Davis & Thompson Law",
      clientEligibility: "Ages 21-75, valid driver's license, recent accident within 30 days, not at fault",
      maxClientsPerIntake: 6,
      priority: 2,
      campaignAssignment: "Auto Claims Recovery 2024",
      bonusPerQualifiedSale: 125.0,
      isActive: true,
      isHidden: false,
      createdAt: "2024-01-08T14:20:00Z",
      updatedAt: "2024-01-19T11:10:00Z",
    },
    {
      id: "form-005",
      alias: "Workers Compensation Claims",
      formstackUrl: "https://formstack.com/forms/workers-comp-intake",
      lawFirmName: "Miller & Associates",
      clientEligibility: "Ages 18-67, workplace injury within 90 days, employed at time of injury",
      maxClientsPerIntake: 3,
      priority: 1,
      campaignAssignment: "Workers Comp Recovery 2024",
      bonusPerQualifiedSale: 175.0,
      isActive: true,
      isHidden: false,
      createdAt: "2024-01-20T09:15:00Z",
      updatedAt: "2024-01-25T11:30:00Z",
    },
  ])

  const [organizations] = useState<Organization[]>([
    {
      id: "org-em-001",
      name: "Eastern Region EM",
      type: "EM",
      parentId: undefined,
      managerId: "mgr-001",
      managerName: "John Smith",
      isActive: true,
      agentCount: 0,
      level: 1,
      children: [
        {
          id: "org-dm-001",
          name: "Northeast DM Team A",
          type: "DM",
          parentId: "org-em-001",
          managerId: "mgr-002",
          managerName: "Sarah Johnson",
          isActive: true,
          agentCount: 0,
          level: 2,
          children: [
            {
              id: "org-cm-001",
              name: "Boston CM Branch",
              type: "CM",
              parentId: "org-dm-001",
              managerId: "mgr-003",
              managerName: "Mike Wilson",
              isActive: true,
              agentCount: 42,
              level: 3,
              children: [
                {
                  id: "org-bm-001",
                  name: "Boston BM Office 1",
                  type: "BM",
                  parentId: "org-cm-001",
                  managerId: "mgr-004",
                  managerName: "Lisa Brown",
                  isActive: true,
                  agentCount: 15,
                  level: 4,
                },
                {
                  id: "org-am-001",
                  name: "Boston AM Team 1",
                  type: "AM",
                  parentId: "org-cm-001",
                  managerId: "mgr-005",
                  managerName: "David Lee",
                  isActive: true,
                  agentCount: 12,
                  level: 4,
                },
                {
                  id: "org-m-001",
                  name: "Boston M Unit 1",
                  type: "M",
                  parentId: "org-cm-001",
                  managerId: "mgr-006",
                  managerName: "Jennifer Davis",
                  isActive: true,
                  agentCount: 15,
                  level: 4,
                },
              ],
            },
            {
              id: "org-cm-002",
              name: "Hartford CM Branch",
              type: "CM",
              parentId: "org-dm-001",
              managerId: "mgr-007",
              managerName: "Robert Taylor",
              isActive: true,
              agentCount: 36,
              level: 3,
              children: [
                {
                  id: "org-bm-002",
                  name: "Hartford BM Office 1",
                  type: "BM",
                  parentId: "org-cm-002",
                  managerId: "mgr-008",
                  managerName: "Amanda White",
                  isActive: true,
                  agentCount: 18,
                  level: 4,
                },
                {
                  id: "org-am-002",
                  name: "Hartford AM Team 1",
                  type: "AM",
                  parentId: "org-cm-002",
                  managerId: "mgr-009",
                  managerName: "Chris Martinez",
                  isActive: true,
                  agentCount: 18,
                  level: 4,
                },
              ],
            },
          ],
        },
        {
          id: "org-dm-002",
          name: "Southeast DM Team B",
          type: "DM",
          parentId: "org-em-001",
          managerId: "mgr-010",
          managerName: "Patricia Garcia",
          isActive: true,
          agentCount: 0,
          level: 2,
          children: [
            {
              id: "org-cm-003",
              name: "Atlanta CM Branch",
              type: "CM",
              parentId: "org-dm-002",
              managerId: "mgr-011",
              managerName: "Kevin Anderson",
              isActive: true,
              agentCount: 78,
              level: 3,
              children: [
                {
                  id: "org-bm-003",
                  name: "Atlanta BM Office 1",
                  type: "BM",
                  parentId: "org-cm-003",
                  managerId: "mgr-012",
                  managerName: "Michelle Thompson",
                  isActive: true,
                  agentCount: 25,
                  level: 4,
                },
                {
                  id: "org-am-003",
                  name: "Atlanta AM Team 1",
                  type: "AM",
                  parentId: "org-cm-003",
                  managerId: "mgr-013",
                  managerName: "Daniel Rodriguez",
                  isActive: true,
                  agentCount: 28,
                  level: 4,
                },
                {
                  id: "org-m-002",
                  name: "Atlanta M Unit 1",
                  type: "M",
                  parentId: "org-cm-003",
                  managerId: "mgr-014",
                  managerName: "Nicole Wilson",
                  isActive: true,
                  agentCount: 25,
                  level: 4,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: "org-em-002",
      name: "Western Region EM",
      type: "EM",
      parentId: undefined,
      managerId: "mgr-015",
      managerName: "Thomas Clark",
      isActive: true,
      agentCount: 0,
      level: 1,
      children: [
        {
          id: "org-dm-003",
          name: "Pacific DM Team C",
          type: "DM",
          parentId: "org-em-002",
          managerId: "mgr-016",
          managerName: "Jessica Lewis",
          isActive: true,
          agentCount: 0,
          level: 2,
          children: [
            {
              id: "org-cm-004",
              name: "Los Angeles CM Branch",
              type: "CM",
              parentId: "org-dm-003",
              managerId: "mgr-017",
              managerName: "Ryan Walker",
              isActive: true,
              agentCount: 70,
              level: 3,
              children: [
                {
                  id: "org-bm-004",
                  name: "LA BM Office 1",
                  type: "BM",
                  parentId: "org-cm-004",
                  managerId: "mgr-018",
                  managerName: "Stephanie Hall",
                  isActive: true,
                  agentCount: 22,
                  level: 4,
                },
                {
                  id: "org-am-004",
                  name: "LA AM Team 1",
                  type: "AM",
                  parentId: "org-cm-004",
                  managerId: "mgr-019",
                  managerName: "Brandon Allen",
                  isActive: true,
                  agentCount: 24,
                  level: 4,
                },
                {
                  id: "org-m-003",
                  name: "LA M Unit 1",
                  type: "M",
                  parentId: "org-cm-004",
                  managerId: "mgr-020",
                  managerName: "Melissa Young",
                  isActive: true,
                  agentCount: 24,
                  level: 4,
                },
              ],
            },
            {
              id: "org-cm-005",
              name: "San Francisco CM Branch",
              type: "CM",
              parentId: "org-dm-003",
              managerId: "mgr-021",
              managerName: "Andrew King",
              isActive: true,
              agentCount: 72,
              level: 3,
              children: [
                {
                  id: "org-bm-005",
                  name: "SF BM Office 1",
                  type: "BM",
                  parentId: "org-cm-005",
                  managerId: "mgr-022",
                  managerName: "Rachel Wright",
                  isActive: true,
                  agentCount: 24,
                  level: 4,
                },
                {
                  id: "org-am-005",
                  name: "SF AM Team 1",
                  type: "AM",
                  parentId: "org-cm-005",
                  managerId: "mgr-023",
                  managerName: "Justin Lopez",
                  isActive: true,
                  agentCount: 24,
                  level: 4,
                },
                {
                  id: "org-m-004",
                  name: "SF M Unit 1",
                  type: "M",
                  parentId: "org-cm-005",
                  managerId: "mgr-024",
                  managerName: "Kimberly Hill",
                  isActive: true,
                  agentCount: 24,
                  level: 4,
                },
              ],
            },
          ],
        },
      ],
    },
  ])

  const [assignments, setAssignments] = useState<FormAssignment[]>([
    {
      id: "assign-001",
      formId: "form-001",
      formAlias: "Mass Tort Gaming Addiction A",
      organizationId: "org-em-001",
      organizationName: "Eastern Region EM",
      organizationType: "EM",
      assignedAt: "2024-01-15T10:30:00Z",
      assignedBy: "FMO CEO John Smith",
      isActive: true,
      isInherited: false,
      directUsersAffected: 0,
      inheritedUsersAffected: 234,
      totalUsersAffected: 234,
    },
    {
      id: "assign-002",
      formId: "form-002",
      formAlias: "Medicare Advantage Enrollment",
      organizationId: "org-cm-001",
      organizationName: "Boston CM Branch",
      organizationType: "CM",
      assignedAt: "2024-01-16T14:20:00Z",
      assignedBy: "FMO CEO John Smith",
      isActive: true,
      isInherited: false,
      directUsersAffected: 42,
      inheritedUsersAffected: 42,
      totalUsersAffected: 84,
    },
    {
      id: "assign-003",
      formId: "form-003",
      formAlias: "Life Insurance Application",
      organizationId: "org-dm-001",
      organizationName: "Northeast DM Team A",
      organizationType: "DM",
      assignedAt: "2024-01-18T09:45:00Z",
      assignedBy: "FMO CEO John Smith",
      isActive: false,
      isInherited: false,
      directUsersAffected: 0,
      inheritedUsersAffected: 120,
      totalUsersAffected: 120,
    },
    {
      id: "assign-004",
      formId: "form-004",
      formAlias: "Auto Insurance Claims",
      organizationId: "org-cm-003",
      organizationName: "Atlanta CM Branch",
      organizationType: "CM",
      assignedAt: "2024-01-20T11:15:00Z",
      assignedBy: "FMO CEO John Smith",
      isActive: true,
      isInherited: false,
      directUsersAffected: 78,
      inheritedUsersAffected: 78,
      totalUsersAffected: 156,
    },
    {
      id: "assign-005",
      formId: "form-005",
      formAlias: "Workers Compensation Claims",
      organizationId: "org-em-002",
      organizationName: "Western Region EM",
      organizationType: "EM",
      assignedAt: "2024-01-22T16:30:00Z",
      assignedBy: "FMO CEO John Smith",
      isActive: true,
      isInherited: false,
      directUsersAffected: 0,
      inheritedUsersAffected: 142,
      totalUsersAffected: 142,
    },
    // Adding more sample data to demonstrate pagination
    {
      id: "assign-006",
      formId: "form-001",
      formAlias: "Mass Tort Gaming Addiction A",
      organizationId: "org-cm-004",
      organizationName: "Los Angeles CM Branch",
      organizationType: "CM",
      assignedAt: "2024-01-23T09:00:00Z",
      assignedBy: "FMO CEO John Smith",
      isActive: true,
      isInherited: false,
      directUsersAffected: 70,
      inheritedUsersAffected: 70,
      totalUsersAffected: 140,
    },
    {
      id: "assign-007",
      formId: "form-002",
      formAlias: "Medicare Advantage Enrollment",
      organizationId: "org-cm-005",
      organizationName: "San Francisco CM Branch",
      organizationType: "CM",
      assignedAt: "2024-01-24T11:30:00Z",
      assignedBy: "FMO CEO John Smith",
      isActive: true,
      isInherited: false,
      directUsersAffected: 72,
      inheritedUsersAffected: 48,
      totalUsersAffected: 120,
    },
    {
      id: "assign-008",
      formId: "form-003",
      formAlias: "Life Insurance Application",
      organizationId: "org-dm-003",
      organizationName: "Pacific DM Team C",
      organizationType: "DM",
      assignedAt: "2024-01-25T14:15:00Z",
      assignedBy: "FMO CEO John Smith",
      isActive: false,
      isInherited: false,
      directUsersAffected: 0,
      inheritedUsersAffected: 190,
      totalUsersAffected: 190,
    },
    {
      id: "assign-009",
      formId: "form-004",
      formAlias: "Auto Insurance Claims",
      organizationId: "org-cm-002",
      organizationName: "Hartford CM Branch",
      organizationType: "CM",
      assignedAt: "2024-01-26T16:45:00Z",
      assignedBy: "FMO CEO John Smith",
      isActive: true,
      isInherited: false,
      directUsersAffected: 36,
      inheritedUsersAffected: 36,
      totalUsersAffected: 72,
    },
    {
      id: "assign-010",
      formId: "form-005",
      formAlias: "Workers Compensation Claims",
      organizationId: "org-dm-002",
      organizationName: "Southeast DM Team B",
      organizationType: "DM",
      assignedAt: "2024-01-27T10:20:00Z",
      assignedBy: "FMO CEO John Smith",
      isActive: true,
      isInherited: false,
      directUsersAffected: 0,
      inheritedUsersAffected: 156,
      totalUsersAffected: 156,
    },
    {
      id: "assign-011",
      formId: "form-001",
      formAlias: "Mass Tort Gaming Addiction A",
      organizationId: "org-dm-001",
      organizationName: "Northeast DM Team A",
      organizationType: "DM",
      assignedAt: "2024-01-28T13:10:00Z",
      assignedBy: "FMO CEO John Smith",
      isActive: false,
      isInherited: false,
      directUsersAffected: 0,
      inheritedUsersAffected: 114,
      totalUsersAffected: 114,
    },
    {
      id: "assign-012",
      formId: "form-002",
      formAlias: "Medicare Advantage Enrollment",
      organizationId: "org-em-002",
      organizationName: "Western Region EM",
      organizationType: "EM",
      assignedAt: "2024-01-29T15:30:00Z",
      assignedBy: "FMO CEO John Smith",
      isActive: true,
      isInherited: false,
      directUsersAffected: 0,
      inheritedUsersAffected: 190,
      totalUsersAffected: 190,
    },
  ])

  // State management
  const [loading, setLoading] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [selectedForm, setSelectedForm] = useState<VendorIntakeForm | null>(null)
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([])
  const [assignmentPreview, setAssignmentPreview] = useState<AssignmentPreview>({
    directUsers: 0,
    inheritedUsers: 0,
    totalUsers: 0,
    affectedOrganizations: [],
    inheritedOrganizations: [],
  })

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all")
  const [typeFilter, setTypeFilter] = useState<"all" | "EM" | "DM" | "CM">("all")
  const [formFilter, setFormFilter] = useState<"all" | string>("all")

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // Organization tree state
  const [expandedOrgs, setExpandedOrgs] = useState<string[]>(["org-em-001", "org-em-002"])

  // Calculate assignment preview when organizations are selected
  useEffect(() => {
    calculateAssignmentPreview()
  }, [selectedOrganizations])

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, statusFilter, typeFilter, formFilter, itemsPerPage])

  const calculateAssignmentPreview = () => {
    if (selectedOrganizations.length === 0) {
      setAssignmentPreview({
        directUsers: 0,
        inheritedUsers: 0,
        totalUsers: 0,
        affectedOrganizations: [],
        inheritedOrganizations: [],
      })
      return
    }

    let directUsers = 0
    let inheritedUsers = 0
    const affectedOrganizations: Organization[] = []
    const inheritedOrganizations: Organization[] = []

    const flattenOrganizations = (orgs: Organization[]): Organization[] => {
      const result: Organization[] = []
      orgs.forEach((org) => {
        result.push(org)
        if (org.children) {
          result.push(...flattenOrganizations(org.children))
        }
      })
      return result
    }

    const allOrgs = flattenOrganizations(organizations)

    selectedOrganizations.forEach((orgId) => {
      const org = allOrgs.find((o) => o.id === orgId)
      if (org) {
        affectedOrganizations.push(org)

        // Direct users (only CM level has direct users for forms)
        if (org.type === "CM") {
          directUsers += org.agentCount
        }

        // Find all child organizations that auto-inherit
        const findInheritedChildren = (parentOrg: Organization) => {
          if (parentOrg.children) {
            parentOrg.children.forEach((child) => {
              if (child.type === "BM" || child.type === "AM" || child.type === "M") {
                inheritedOrganizations.push(child)
                inheritedUsers += child.agentCount
              }
              findInheritedChildren(child)
            })
          }
        }

        findInheritedChildren(org)
      }
    })

    setAssignmentPreview({
      directUsers,
      inheritedUsers,
      totalUsers: directUsers + inheritedUsers,
      affectedOrganizations,
      inheritedOrganizations,
    })
  }

  const handleAssignForm = async () => {
    if (!selectedForm || selectedOrganizations.length === 0) return

    try {
      setLoading(true)

      // Create new assignments
      const newAssignments: FormAssignment[] = selectedOrganizations.map((orgId) => {
        const org = flattenOrganizations(organizations).find((o) => o.id === orgId)
        if (!org) throw new Error(`Organization not found: ${orgId}`)

        return {
          id: `assign-${Date.now()}-${orgId}`,
          formId: selectedForm.id,
          formAlias: selectedForm.alias,
          organizationId: org.id,
          organizationName: org.name,
          organizationType: org.type,
          assignedAt: new Date().toISOString(),
          assignedBy: "FMO CEO John Smith",
          isActive: true,
          isInherited: false,
          directUsersAffected: org.type === "CM" ? org.agentCount : 0,
          inheritedUsersAffected: calculateInheritedUsers(org),
          totalUsersAffected: (org.type === "CM" ? org.agentCount : 0) + calculateInheritedUsers(org),
        }
      })

      setAssignments([...assignments, ...newAssignments])
      setShowAssignModal(false)
      setSelectedForm(null)
      setSelectedOrganizations([])

      alert(`Successfully assigned "${selectedForm.alias}" to ${selectedOrganizations.length} organizations.`)
    } catch (error) {
      console.error("Error assigning form:", error)
      alert("Error assigning form. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const calculateInheritedUsers = (org: Organization): number => {
    let inheritedUsers = 0
    if (org.children) {
      org.children.forEach((child) => {
        if (child.type === "BM" || child.type === "AM" || child.type === "M") {
          inheritedUsers += child.agentCount
        }
        inheritedUsers += calculateInheritedUsers(child)
      })
    }
    return inheritedUsers
  }

  const flattenOrganizations = (orgs: Organization[]): Organization[] => {
    const result: Organization[] = []
    orgs.forEach((org) => {
      result.push(org)
      if (org.children) {
        result.push(...flattenOrganizations(org.children))
      }
    })
    return result
  }

  const toggleAssignmentStatus = (assignmentId: string) => {
    setAssignments(
      assignments.map((assignment) =>
        assignment.id === assignmentId ? { ...assignment, isActive: !assignment.isActive } : assignment,
      ),
    )
  }

  const deleteAssignment = (assignmentId: string) => {
    if (window.confirm("Are you sure you want to remove this form assignment?")) {
      setAssignments(assignments.filter((assignment) => assignment.id !== assignmentId))
    }
  }

  const toggleOrgExpansion = (orgId: string) => {
    setExpandedOrgs((prev) => (prev.includes(orgId) ? prev.filter((id) => id !== orgId) : [...prev, orgId]))
  }

  const renderOrganizationTree = (orgs: Organization[], level = 0) => {
    return orgs.map((org) => (
      <div key={org.id} className={`${level > 0 ? "ml-6" : ""}`}>
        <div className="flex items-center p-3 border border-gray-200 rounded-lg mb-2 bg-white hover:bg-gray-50">
          <div className="flex items-center flex-1">
            <input
              type="checkbox"
              id={`org-${org.id}`}
              checked={selectedOrganizations.includes(org.id)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedOrganizations([...selectedOrganizations, org.id])
                } else {
                  setSelectedOrganizations(selectedOrganizations.filter((id) => id !== org.id))
                }
              }}
              disabled={org.type === "BM" || org.type === "AM" || org.type === "M"}
              className="mr-3"
            />

            <div className="flex items-center gap-2 flex-1">
              {org.children && org.children.length > 0 && (
                <button onClick={() => toggleOrgExpansion(org.id)} className="p-1 hover:bg-gray-200 rounded">
                  {expandedOrgs.includes(org.id) ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronUp className="w-4 h-4" />
                  )}
                </button>
              )}

              <Building2 className="w-4 h-4 text-blue-600" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{org.name}</span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      org.type === "EM"
                        ? "bg-purple-100 text-purple-800"
                        : org.type === "DM"
                          ? "bg-blue-100 text-blue-800"
                          : org.type === "CM"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {org.type}
                  </span>
                  {(org.type === "BM" || org.type === "AM" || org.type === "M") && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 flex items-center gap-1">
                      <ArrowDown className="w-3 h-3" />
                      Auto-Inherit
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>{org.agentCount} agents</span>
                  <span>Manager: {org.managerName}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {org.children && expandedOrgs.includes(org.id) && renderOrganizationTree(org.children, level + 1)}
      </div>
    ))
  }

  // Filter assignments
  const filteredAssignments = assignments.filter((assignment) => {
    const matchesSearch =
      assignment.formAlias.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.organizationName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.assignedBy.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && assignment.isActive) ||
      (statusFilter === "inactive" && !assignment.isActive)

    const matchesType = typeFilter === "all" || assignment.organizationType === typeFilter

    const matchesForm = formFilter === "all" || assignment.formId === formFilter

    return matchesSearch && matchesStatus && matchesType && matchesForm
  })

  // Pagination calculations
  const totalItems = filteredAssignments.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems)
  const currentAssignments = filteredAssignments.slice(startIndex, endIndex)

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)))
  }

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  // Generate page numbers for pagination with ellipsis
  const getPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      const halfVisible = Math.floor(maxVisiblePages / 2)
      let startPage = Math.max(1, currentPage - halfVisible)
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1)
      }

      // Always show first page
      if (startPage > 1) {
        pages.push(1)
        if (startPage > 2) {
          pages.push("...")
        }
      }

      // Show middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i)
      }

      // Always show last page
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push("...")
        }
        pages.push(totalPages)
      }
    }

    return pages
  }

  const getAssignmentStats = () => {
    const totalAssignments = assignments.length
    const activeAssignments = assignments.filter((a) => a.isActive).length
    const totalUsersAffected = assignments.reduce((sum, a) => sum + a.totalUsersAffected, 0)
    const emAssignments = assignments.filter((a) => a.organizationType === "EM").length
    const dmAssignments = assignments.filter((a) => a.organizationType === "DM").length
    const cmAssignments = assignments.filter((a) => a.organizationType === "CM").length

    return {
      totalAssignments,
      activeAssignments,
      totalUsersAffected,
      emAssignments,
      dmAssignments,
      cmAssignments,
    }
  }

  const stats = getAssignmentStats()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Vendor Form Assignment Management</h2>
          <p className="text-gray-600">Assign vendor intake forms to organizations with automatic inheritance rules</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowAssignModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Assign Form</span>
          </button>
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2">
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Assignment Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Assignments</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalAssignments}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Assignments</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeAssignments}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Users Affected</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalUsersAffected.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">EM Assignments</p>
              <p className="text-2xl font-bold text-gray-900">{stats.emAssignments}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">DM Assignments</p>
              <p className="text-2xl font-bold text-gray-900">{stats.dmAssignments}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <GitBranch className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">CM Assignments</p>
              <p className="text-2xl font-bold text-gray-900">{stats.cmAssignments}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Assignment Rules Overview */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Assignment Rules & Inheritance</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Direct Assignment Levels
            </h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2 p-2 bg-white rounded border">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span className="text-sm font-medium">EM - Executive Manager</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-white rounded border">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-sm font-medium">DM - District Manager</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-white rounded border">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium">CM - Campaign Manager</span>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-medium text-yellow-900 mb-3 flex items-center gap-2">
              <ArrowDown className="w-5 h-5" />
              Auto-Inheritance Levels
            </h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2 p-2 bg-white rounded border border-dashed border-yellow-300">
                <ArrowDown className="w-3 h-3 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-800">BM - Branch Manager</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-white rounded border border-dashed border-yellow-300">
                <ArrowDown className="w-3 h-3 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-800">AM - Area Manager</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-white rounded border border-dashed border-yellow-300">
                <ArrowDown className="w-3 h-3 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-800">M - Manager</span>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-medium text-green-900 mb-3 flex items-center gap-2">
              <Info className="w-5 h-5" />
              Key Rules
            </h4>
            <div className="space-y-2 text-sm text-green-800">
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-green-600 rounded-full mt-1.5"></div>
                <span>Only EM, DM, CM can receive direct assignments</span>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-green-600 rounded-full mt-1.5"></div>
                <span>BM, AM, M automatically inherit from parent CM</span>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-green-600 rounded-full mt-1.5"></div>
                <span>Inheritance is immediate and automatic</span>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-green-600 rounded-full mt-1.5"></div>
                <span>Deactivating assignment affects all inherited levels</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search assignments by form, organization, or assigned by..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as "all" | "active" | "inactive")}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as "all" | "EM" | "DM" | "CM")}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="EM">EM Only</option>
              <option value="DM">DM Only</option>
              <option value="CM">CM Only</option>
            </select>
            <select
              value={formFilter}
              onChange={(e) => setFormFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Forms</option>
              {forms.map((form) => (
                <option key={form.id} value={form.id}>
                  {form.alias}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Results Counter */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-600">
          Showing {totalItems > 0 ? startIndex + 1 : 0} to {endIndex} of {totalItems} assignments
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Show:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="text-sm text-gray-600">per page</span>
        </div>
      </div>

      {/* Assignments Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Form & Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assignment Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User Impact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentAssignments.map((assignment) => (
                <tr key={assignment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-900">{assignment.formAlias}</p>
                        <ExternalLink className="w-3 h-3 text-gray-400" />
                      </div>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-gray-600">{assignment.organizationName}</span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            assignment.organizationType === "EM"
                              ? "bg-purple-100 text-purple-800"
                              : assignment.organizationType === "DM"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-green-100 text-green-800"
                          }`}
                        >
                          {assignment.organizationType}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <p className="text-gray-900 font-medium">
                        Assigned: {new Date(assignment.assignedAt).toLocaleDateString()}
                      </p>
                      <p className="text-gray-600">By: {assignment.assignedBy}</p>
                      <p className="text-gray-500 text-xs">
                        {assignment.isInherited ? "Inherited Assignment" : "Direct Assignment"}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="flex justify-between mb-1">
                        <span className="text-gray-600">Direct:</span>
                        <span className="font-medium">{assignment.directUsersAffected}</span>
                      </div>
                      <div className="flex justify-between mb-1">
                        <span className="text-gray-600">Inherited:</span>
                        <span className="font-medium">{assignment.inheritedUsersAffected}</span>
                      </div>
                      <div className="flex justify-between pt-1 border-t border-gray-200">
                        <span className="text-gray-900 font-medium">Total:</span>
                        <span className="font-bold text-blue-600">{assignment.totalUsersAffected}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        assignment.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                      }`}
                    >
                      {assignment.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => toggleAssignmentStatus(assignment.id)}
                        className={`${assignment.isActive ? "text-red-600 hover:text-red-900" : "text-green-600 hover:text-green-900"}`}
                        title={assignment.isActive ? "Deactivate" : "Activate"}
                      >
                        {assignment.isActive ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => deleteAssignment(assignment.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Assignment"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {currentAssignments.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Form Assignments Found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || statusFilter !== "all" || typeFilter !== "all" || formFilter !== "all"
                ? "No assignments match your current filters. Try adjusting your search criteria."
                : "No vendor intake forms have been assigned to organizations yet."}
            </p>
            {!searchTerm && statusFilter === "all" && typeFilter === "all" && formFilter === "all" && (
              <button
                onClick={() => setShowAssignModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Assign First Form
              </button>
            )}
          </div>
        )}
      </div>

      {/* Enhanced Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-6 py-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages} ({totalItems} total assignments)
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First Page"
              >
                First
              </button>

              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>

              <div className="flex gap-1">
                {getPageNumbers().map((pageNum, index) => (
                  <button
                    key={index}
                    onClick={() => typeof pageNum === "number" && handlePageChange(pageNum)}
                    disabled={pageNum === "..."}
                    className={`px-3 py-2 text-sm rounded-lg ${
                      currentPage === pageNum
                        ? "bg-blue-600 text-white"
                        : pageNum === "..."
                          ? "text-gray-400 cursor-default"
                          : "border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>

              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last Page"
              >
                Last
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Form Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Assign Vendor Intake Form to Organizations</h3>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Form Selection */}
                <div className="lg:col-span-2">
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3">1. Select Form to Assign</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-4">
                      {forms
                        .filter((form) => form.isActive && !form.isHidden)
                        .map((form) => (
                          <div
                            key={form.id}
                            className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                              selectedForm?.id === form.id
                                ? "border-blue-500 bg-blue-50"
                                : "border-gray-200 hover:bg-gray-50"
                            }`}
                            onClick={() => setSelectedForm(form)}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="radio"
                                name="selectedForm"
                                checked={selectedForm?.id === form.id}
                                onChange={() => setSelectedForm(form)}
                                className="text-blue-600"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">{form.alias}</span>
                                  <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                    Priority {form.priority}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600">{form.campaignAssignment}</p>
                                <p className="text-xs text-gray-500">Bonus: ${form.bonusPerQualifiedSale}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {selectedForm && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">
                        2. Select Organizations (EM, DM, CM levels only)
                      </h4>
                      <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                        {renderOrganizationTree(organizations)}
                      </div>

                      <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                        <h5 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                          <Info className="w-4 h-4" />
                          Assignment Rules
                        </h5>
                        <ul className="text-sm text-blue-800 space-y-1">
                          <li>• Only EM, DM, CM levels can be directly assigned</li>
                          <li>• BM, AM, M levels automatically inherit from their parent CM</li>
                          <li>• Inheritance is immediate and automatic</li>
                          <li>• All affected users will have access to this form</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {/* Assignment Preview */}
                <div className="lg:col-span-1">
                  <h4 className="font-medium text-gray-900 mb-3">Assignment Preview</h4>

                  {!selectedForm ? (
                    <div className="p-4 bg-gray-50 rounded-lg text-center">
                      <AlertTriangle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Select a form to see assignment preview</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h5 className="font-medium text-gray-900 mb-3">Selected Form</h5>
                        <div className="text-sm">
                          <p className="font-medium text-blue-600">{selectedForm.alias}</p>
                          <p className="text-gray-600">{selectedForm.campaignAssignment}</p>
                          <p className="text-gray-500">Priority: {selectedForm.priority}</p>
                        </div>
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h5 className="font-medium text-gray-900 mb-3">Impact Summary</h5>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Direct Users:</span>
                            <span className="font-medium">{assignmentPreview.directUsers}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Inherited Users:</span>
                            <span className="font-medium">{assignmentPreview.inheritedUsers}</span>
                          </div>
                          <div className="flex justify-between pt-2 border-t border-gray-300">
                            <span className="font-medium">Total Affected:</span>
                            <span className="font-bold text-blue-600">{assignmentPreview.totalUsers}</span>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 bg-green-50 rounded-lg">
                        <h5 className="font-medium text-green-900 mb-3">Selected Organizations</h5>
                        <div className="space-y-1 text-sm text-green-800 max-h-32 overflow-y-auto">
                          {selectedOrganizations.length === 0 ? (
                            <p className="text-green-600">No organizations selected</p>
                          ) : (
                            selectedOrganizations.map((orgId) => {
                              const org = flattenOrganizations(organizations).find((o) => o.id === orgId)
                              return (
                                <div key={orgId} className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                                  <span>{org?.name}</span>
                                  <span className="text-xs">({org?.type})</span>
                                </div>
                              )
                            })
                          )}
                        </div>
                      </div>

                      {assignmentPreview.inheritedOrganizations.length > 0 && (
                        <div className="p-4 bg-yellow-50 rounded-lg">
                          <h5 className="font-medium text-yellow-900 mb-3 flex items-center gap-2">
                            <ArrowDown className="w-4 h-4" />
                            Auto-Inherited Organizations
                          </h5>
                          <div className="space-y-1 text-sm text-yellow-800 max-h-32 overflow-y-auto">
                            {assignmentPreview.inheritedOrganizations.map((org, index) => (
                              <div key={index} className="flex items-center gap-2">
                                <ArrowDown className="w-3 h-3 text-yellow-600" />
                                <span>{org.name}</span>
                                <span className="text-xs">({org.type})</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowAssignModal(false)
                    setSelectedForm(null)
                    setSelectedOrganizations([])
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignForm}
                  disabled={!selectedForm || selectedOrganizations.length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Assign Form ({selectedOrganizations.length} organizations)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
